import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Circle, Clock, Award } from "lucide-react";
import type { CertificationStatus } from "../types";

interface CertificationProgressProps {
  certification?: CertificationStatus;
  environment: "certification" | "production";
}

const CERTIFICATION_STEPS = [
  { id: "step_1_test_set", label: "Set de Pruebas", description: "Generación de documentos de prueba" },
  { id: "step_2_simulation", label: "Simulación", description: "Envío a ambiente de pruebas" },
  { id: "step_3_exchange", label: "Intercambio", description: "Verificación de recepción" },
  { id: "step_4_print", label: "Impresión", description: "Validación de impresión" },
  { id: "step_5_declaration", label: "Declaración", description: "Envío a SII" },
  { id: "step_6_pending", label: "Esperando Aprobación", description: "Revisión por SII (~10 días)" },
];

const STATUS_ORDER = [
  "not_started",
  "step_1_test_set",
  "step_2_simulation",
  "step_3_exchange",
  "step_4_print",
  "step_5_declaration",
  "step_6_pending",
  "completed",
];

/**
 * Component to display SII certification workflow progress.
 */
export function CertificationProgress({
  certification,
  environment,
}: CertificationProgressProps) {
  const currentStatus = certification?.status ?? "not_started";
  const currentStepIndex = STATUS_ORDER.indexOf(currentStatus);

  const isCompleted = currentStatus === "completed";
  const isProductionReady = environment === "production" || isCompleted;

  return (
    <div className="space-y-6">
      {/* Status Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isProductionReady ? (
              <Award className="h-5 w-5 text-green-500" />
            ) : (
              <Clock className="h-5 w-5 text-yellow-500" />
            )}
            {isProductionReady ? "Certificado" : "En Proceso de Certificación"}
          </CardTitle>
          <CardDescription>
            {isProductionReady
              ? "Su empresa está autorizada para emitir documentos tributarios electrónicos"
              : "Complete los pasos para obtener la certificación SII"}
          </CardDescription>
        </CardHeader>
        {certification?.approvedAt && (
          <CardContent>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Aprobado: </span>
                <span className="font-medium">
                  {new Date(certification.approvedAt).toLocaleDateString()}
                </span>
              </div>
              {certification.attentionNumber && (
                <div>
                  <span className="text-muted-foreground">Nº Atención: </span>
                  <span className="font-medium">{certification.attentionNumber}</span>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Steps Progress */}
      {!isProductionReady && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Progreso de Certificación</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {CERTIFICATION_STEPS.map((step, index) => {
                const stepIndex = STATUS_ORDER.indexOf(step.id);
                const isComplete = stepIndex < currentStepIndex;
                const isCurrent = step.id === currentStatus;

                return (
                  <div key={step.id} className="flex items-start gap-4">
                    {/* Step Icon */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`
                          flex items-center justify-center w-8 h-8 rounded-full
                          ${isComplete ? "bg-green-500 text-white" : ""}
                          ${isCurrent ? "bg-primary text-primary-foreground" : ""}
                          ${!isComplete && !isCurrent ? "bg-muted text-muted-foreground" : ""}
                        `}
                      >
                        {isComplete ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <span className="text-sm font-medium">{index + 1}</span>
                        )}
                      </div>
                      {index < CERTIFICATION_STEPS.length - 1 && (
                        <div
                          className={`w-0.5 h-8 ${
                            isComplete ? "bg-green-500" : "bg-muted"
                          }`}
                        />
                      )}
                    </div>

                    {/* Step Content */}
                    <div className="flex-1 pb-4">
                      <p
                        className={`font-medium ${
                          isCurrent ? "text-primary" : ""
                        } ${!isComplete && !isCurrent ? "text-muted-foreground" : ""}`}
                      >
                        {step.label}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {step.description}
                      </p>
                      {isCurrent && certification?.startedAt && step.id === "step_6_pending" && (
                        <p className="text-sm text-yellow-600 mt-1">
                          Enviado: {new Date(certification.startedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info for MVP */}
      {!isProductionReady && (
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground text-center">
              El proceso de certificación guiado estará disponible próximamente.
              Por ahora, puede completar la certificación manualmente a través del portal SII.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
